/**
 * Client filing defaults (Batch Filing Workflow checklist #8).
 *
 * MISC sets, per client, the values that never vary for that client so filing can bypass extraction
 * for them (Kelly, 2026-07-14): a default **carrier** (+ NAIC the system resolves), a default
 * **coverage code per state**, and — for the four "special-license" states (FL / CA / WA / NJ) — a
 * default **agency / state license**. Blank means "fall back to extraction / the License Matrix", so a
 * value here is an override that MISC can always revert.
 *
 * ── Endpoint contract (this FE is built to it; the sibling internal-api PR implements the backend) ──
 *
 *   GET  /AutomaticFiling/clientFilingDefaults?partnersId=<id>
 *     → { status: true, data: ClientFilingDefaults }
 *       carrier.naicDefault / states[].coverageDefault / states[].licenseDefault are the values the
 *       system already knows (License Matrix / extraction), shown so MISC can revert to them; the
 *       *Override fields are what MISC set (null = not overridden → use the default).
 *
 *   POST /AutomaticFiling/clientFilingDefaults
 *     body { partnersId, carrier?: { name, naic } | null,
 *            states?: [{ stateId, coverageOverride?: string|null, licenseOverride?: string|null }] }
 *     → { status: true }   (idempotent upsert; a null/blank override clears it → revert to default)
 *
 * Until the backend ships, GET returns 404 → the panel still renders (scaffolded from the live filing
 * states) with a "saving not yet enabled" banner, so the layout is reviewable and wiring is one flip.
 */
import { apiClient } from './api';

/** The only states that need an agency / state-specific license beyond the NPN (Kelly, 2026-07-14:
 *  "only 4 special states"). Every other filing state files on the NPN, which is already complete. */
export const SPECIAL_LICENSE_STATES = ['FL', 'CA', 'WA', 'NJ'];

export interface StateFilingDefault {
    stateId: number;
    stateName: string;
    postalAbbv: string;
    /** MISC-set coverage code (null = use extraction / the default). */
    coverageOverride: string | null;
    /** System default coverage code (from extraction), shown so MISC can revert. */
    coverageDefault: string | null;
    coverageDefaultName?: string | null;
    /** MISC-set agency / state license (special-license states only; null = use the License Matrix). */
    licenseOverride: string | null;
    /** License Matrix default, shown so MISC can revert. */
    licenseDefault: string | null;
    /** True for FL/CA/WA/NJ — the row shows a license editor as well as coverage. */
    special: boolean;
}

export interface ClientFilingDefaults {
    partnersId: number;
    /** Client-wide default carrier (applies to every state) — null when unset. */
    carrierName: string | null;
    /** NAIC MISC set / the system resolved for the carrier. */
    carrierNaic: string | null;
    /** NAIC the system resolves from the carrier, shown so MISC can revert. */
    carrierNaicDefault?: string | null;
    states: StateFilingDefault[];
}

export interface FilingCapabilityState {
    stateId: number;
    postalAbbv: string;
    name: string;
    filingEnabled: boolean;
}

/** Load the filing-enabled states (source of the per-state rows) from the capability registry. */
export async function getFilingEnabledStates(token?: string): Promise<FilingCapabilityState[]> {
    const res: any = await apiClient('/AutomaticFiling/getFilingCapabilities', {
        headers: { Authorization: `Bearer ${token}` },
    });
    const rows = Array.isArray(res?.data) ? res.data : [];
    return rows
        .filter((c: any) => c.filingEnabled && c.stateId != null)
        .map((c: any) => ({ stateId: c.stateId, postalAbbv: String(c.postalAbbv || '').toUpperCase(), name: c.name, filingEnabled: true }));
}

export interface GetDefaultsResult {
    ok: boolean;
    /** True when the backend isn't available yet (e.g. 404) — the panel shows a "saving pending" banner. */
    unavailable: boolean;
    data?: ClientFilingDefaults;
}

export async function getClientFilingDefaults(token: string | undefined, partnersId: number): Promise<GetDefaultsResult> {
    const res: any = await apiClient(`/AutomaticFiling/clientFilingDefaults?partnersId=${partnersId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    // apiClient returns { ok:false, status, ... } on an HTTP error, the camelCased body on success, or
    // undefined on a network error. Only a network error / HTTP failure (incl. 404 pre-deploy) means the
    // service is unavailable — a live 200 with no saved defaults yet is fine (the panel scaffolds).
    if (!res || res.ok === false) return { ok: false, unavailable: true };
    if (!res.data) return { ok: true, unavailable: false };
    return { ok: true, unavailable: false, data: res.data as ClientFilingDefaults };
}

export interface SaveDefaultsPayload {
    partnersId: number;
    carrier?: { name: string | null; naic: string | null } | null;
    states?: { stateId: number; coverageOverride?: string | null; licenseOverride?: string | null }[];
}

export interface SaveDefaultsResult { ok: boolean; unavailable: boolean; message?: string }

export async function saveClientFilingDefaults(token: string | undefined, payload: SaveDefaultsPayload): Promise<SaveDefaultsResult> {
    const res: any = await apiClient('/AutomaticFiling/clientFilingDefaults', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!res) return { ok: false, unavailable: false, message: 'Network error — try again.' };
    if (res.ok === false) {
        return { ok: false, unavailable: res.status === 404, message: res.message || `Save failed (HTTP ${res.status}).` };
    }
    return { ok: true, unavailable: false };
}
